import { CSSProperties, useState, useEffect } from 'react';

import { Link } from 'react-router-dom';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { StudentLessonFragment } from '@school_ify/schoolify-repository/src/student/types';

import { ReactComponent as LinkIcon } from 'assets/icons/menu/link.svg';
import { ReactComponent as ArrowDown } from 'assets/icons/arrow-down.svg';
import { ReactComponent as MoreIcon } from 'assets/icons/more-vertical.svg';
import { ReactComponent as InfoIcon } from 'assets/icons/task/task-info.svg';
import useClasses from 'application/dashboard/classes/useClasses';
import StyledTooltip from 'components/StyledTooltip/StyledTooltip';

import { extractDaysOfTheMonth, getDaysArray } from './CalendarUtils';

import './Calendar.scoped.scss';

interface DaysObject {
  dayOfTheMonth: number;
  date: Date;
  day: string;
}

interface Props {
  children?: React.ReactNode;
  style?: CSSProperties;
  classes?: Array<StudentLessonFragment>;
}

const Calendar: React.FC<Props> = ({ style }) => {
  const [, setSelected] = useState(0);
  const [daysOfWeek, setDaysOfWeek] = useState<Array<DaysObject>>();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [, setDaysOfTheMonth] = useState<Array<number>>([]);

  const { getLessons, lessons } = useClasses();
  const { i18n } = useTranslation();

  const lang = i18n.language;

  useEffect(() => {
    getLessons({ date: DateTime.fromISO(currentDate.toISOString()).setLocale(i18n.language) });
  }, [currentDate]);

  useEffect(() => {
    const currentWeekDay = currentDate.getDay();
    const lessDays = currentWeekDay === 0 ? 6 : currentWeekDay - 1;
    const wkStart = new Date(new Date(currentDate).setDate(currentDate.getDate() - lessDays));
    const wkEnd = new Date(new Date(wkStart).setDate(wkStart.getDate() + 6));

    const rangeOfDays = getDaysArray(wkStart, wkEnd, lang);
    setDaysOfWeek(rangeOfDays);
    setDaysOfTheMonth(extractDaysOfTheMonth(currentDate));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, lang]);

  function moveCalendarDate(day: 'ahead' | 'behind' | 'today' | Date) {
    if (day instanceof Date) {
      setCurrentDate(day);
    } else if (day === 'today') {
      setCurrentDate(new Date());
      // setTheDate = today;
    } else {
      // move one week ahead or behind
      const setTheDate = day === 'ahead' ? currentDate.getDate() + 7 : currentDate.getDate() - 7;
      const newDate = new Date(new Date(currentDate).setDate(setTheDate));
      setCurrentDate(newDate);
    }
    // const newDate = new Date(new Date(currentDate).setDate(setTheDate));
    // if (day !== 'today') setCurrentDate(newDate);
  }

  const dt = new Date();
  const onejan = new Date(currentDate.getFullYear(), 0, 1);
  const week = Math.ceil(
    ((currentDate.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7
  );
  const weekFormated = week - 1;

  return (
    <div className="calendar-container" style={style}>
      <div className="calendar-main-container">
        <div className="calendar-main-header">
          <div className="date-container">
            <p>
              {`${
                DateTime.fromISO(currentDate.toISOString()).setLocale(i18n.language).weekdayShort
              } ${currentDate.getDate()}  / ${
                DateTime.fromISO(currentDate.toISOString()).setLocale(i18n.language).monthShort
              }`}{' '}
              ({t('common:week')} {weekFormated})
            </p>
          </div>
          <div className={'flex gap-1rem align-center'}>
            <div className="arrow-container">
              <ArrowDown
                className="arrow-left pointer"
                onClick={() => moveCalendarDate('behind')}
                style={{ width: 20, height: 30 }}
              />
            </div>
            <button className="today-button" onClick={() => moveCalendarDate('today')}>
              {t('common:today')}
            </button>
            <div className="arrow-container">
              <ArrowDown
                className="arrow-right pointer"
                onClick={() => moveCalendarDate('ahead')}
                style={{ width: 22, height: 22 }}
              />
            </div>
          </div>

          {/* <div className="arrow-container">
            <ArrowDown
              className="arrow-right pointer"
              onClick={() => moveCalendarDate('ahead')}
              style={{ width: 20, height: 20 }}
            />
          </div> */}
        </div>
        <div className="calendar-main-divider" />
        <div className="calendar-main-body">
          <div className="calendar-days-column">
            {daysOfWeek &&
              daysOfWeek.map(e => (
                <div className="day-date-container" key={e.dayOfTheMonth}>
                  <div
                    className={`${
                      currentDate.getDate() === e.dayOfTheMonth && 'current'
                    }  calender-week`}
                    key={e.dayOfTheMonth}
                  >
                    <div className="days-containers">
                      <p style={{ marginBottom: '10px' }}>
                        {
                          lang === 'de'
                            ? e.day.slice(0, 2) // showing two letters on calendar
                            : e.day.slice(0, 1) // showing only first letter
                        }
                      </p>
                    </div>

                    <div className="date-container">
                      <div
                        className={`${
                          currentDate.getDate() === e.dayOfTheMonth
                            ? 'background-date'
                            : 'background'
                        }`}
                      >
                        <p
                          style={{
                            fontSize: '18px',
                            cursor: 'pointer',
                            color: `${
                              currentDate.getDate() === e.dayOfTheMonth ? '#fff' : '#213960'
                            }`,
                            fontWeight: 600,
                          }}
                          onClick={() => moveCalendarDate(e.date)}
                        >
                          {e.dayOfTheMonth}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className="classes-in-time">
        {lessons &&
          lessons.length > 0 &&
          lessons.map((singleClass, i) => {
            const isToday = dt.getDate() === currentDate.getDate();
            const time = new Date(`${singleClass.start_date} ${singleClass.start_at}`);
            const hours = time.getHours() < 10 ? `0${time.getHours()}` : time.getHours();
            const minutes = time.getMinutes() < 10 ? `0${time.getMinutes()}` : time.getMinutes();
            const lessonStart = `${hours}:${minutes}`;
            const timeNow = dt.getHours();
            const isHappening = timeNow === hours && isToday;
            const isOver = timeNow > hours && isToday;

            return (
              <div
                className={`class-element ${isHappening && isToday && 'active'}`}
                key={i}
                onClick={() => setSelected(i)}
              >
                <div className="class-name">
                  <p className="class-subject-hours">{lessonStart}</p>
                  <p className="class-subject-name">{singleClass.course.subject.name}</p>
                  <StyledTooltip
                    direction="down-start"
                    tipContentHover
                    tagName="div"
                    content={
                      <div>
                        <p>
                          <span className="info-key">{t('common:physical_classroom')}: </span>
                          {'No. 3'}
                        </p>
                        <p>
                          <span className="info-key">{t('live_classes:Teacher')}: </span>Gina
                          Altenwert
                        </p>
                        <p>{t('common:online_school')}</p>
                        <p>
                          <span className="info-key">Status: </span>
                          {isHappening
                            ? t('common:happening')
                            : isOver
                            ? t('common:over')
                            : t('common:planned')}
                        </p>
                      </div>
                    }
                  >
                    <div className="info-icon pointer">
                      <InfoIcon
                        style={{
                          width: 18,
                          height: 18,
                        }}
                      />
                    </div>
                  </StyledTooltip>
                </div>
                <StyledTooltip
                  direction="left"
                  eventToggle="onClick"
                  tagName="div"
                  eventOn="onClick"
                  closeOnOutsideClick
                  content={
                    <div>
                      <div className="dropdown-link">
                        <Link to="/live-class">
                          <>{t('common:go_to_live_classes')}</>
                        </Link>
                        <LinkIcon />
                      </div>
                      <div className="dropdown-link">
                        <Link to="/study">
                          <>{t('common:go_to_study_materials')}</>
                        </Link>
                        <LinkIcon />
                      </div>
                      <div className="dropdown-link">
                        <Link to="/chat">
                          <>{t('common:go_to_chat')}</>
                        </Link>
                        <LinkIcon />
                      </div>
                    </div>
                  }
                >
                  <div className="dropdown-dots pointer">
                    <MoreIcon style={{ width: 18, height: 28, stroke: '#213960' }} />
                  </div>
                </StyledTooltip>
                {/* <p>{singleClass.start_at}</p>

                <div className="bold">
                  <p>{singleClass.name}</p>
                </div>
                <div className="info-icon">
                  <InfoIcon
                    style={{
                      width: 16,
                      height: 16,
                      marginLeft: 5,
                      stroke: true ? 'black' : isBlue ? '#176BFB' : '',
                      opacity: isBlue ? 0.5 : '',
                    }}
                  />
                  <div className="drop-down">
                    <p>Physical classroom: 17</p>
                    <p>Gina Altenwert</p>
                    <p>Online and school</p>
                    <p>Status: {isBlue ? 'Heppening' : 'Planned'}</p>
                  </div>
                </div>
                <DropDown
                  boxStyle={{ maxWidth: '200px' }}
                  icon={<MoreIcon style={{ width: 18, height: 18 }} />}
                >
                  <div>
                    <Link to="/live-class">Go to Live Class</Link>
                  </div>
                  <div>
                    <Link to="/study">Go to Study Materials</Link>
                  </div>
                  <div>
                    <Link to="/chat">Go to Chat</Link>
                  </div>
                </DropDown> */}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default Calendar;
